import { useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import { useMemo } from 'react';
import {
  HotFilterGroupConfiguration,
  HotFilterTypes,
  sizeHotfilterFacetFields,
} from '@config/catalog/hotfilter-group-configurations';
import { facetToFilterRename } from '@maps/catalog/search-product-facets-filters-maps';
import { ElasticSearch } from '@interfaces/models/elasticSearch';
import { CatalogCategoryHotfilterContext } from '@context/catalog-category-hotfilter.context';
import { CatalogMaterialHotfilterContext } from '@context/catalog-material-hotfilter.context';

const getSizeFilterSelectionCount = (filters: ElasticSearch['filters']) => {
  const sizeFilters = sizeHotfilterFacetFields.map((facet) => facetToFilterRename(facet));
  return sizeFilters.reduce((acc, filterName) => {
    const filtersApplied = filters[filterName];
    if (filtersApplied && Array.isArray(filtersApplied)) {
      return acc + filtersApplied.length;
    }
    return acc;
  }, 0);
};

const useCatalogHotfilterItem = ({ filterGroup }: { filterGroup: HotFilterGroupConfiguration }) => {
  const filters = useContextSelector(CatalogContext, (v) => v.filters ?? {});
  const setFilters = useContextSelector(CatalogContext, (v) => v.setFilters);
  const areMySizesEnabled = useContextSelector(CatalogContext, (v) => v.areMySizesEnabled);
  const setMySizesState = useContextSelector(CatalogContext, (v) => v.setMySizesState);
  const staticFilterGroups = useContextSelector(CatalogContext, (v) => v.staticFilterGroups);

  const removeAllCategoryHotfilters = useContextSelector(
    CatalogCategoryHotfilterContext,
    (v) => v.removeAllCategoryHotfilters,
  );
  const categoryHotfilterCount = useContextSelector(CatalogCategoryHotfilterContext, (v) => v.categoryHotfilterCount);

  const removeAllMaterialHotfilters = useContextSelector(
    CatalogMaterialHotfilterContext,
    (v) => v.removeAllMaterialHotfilters,
  );
  const materialHotfilterCount = useContextSelector(CatalogMaterialHotfilterContext, (v) => v.materialHotfilterCount);

  const checkedItemCount = useMemo(() => {
    if (filterGroup.type === HotFilterTypes.ITEM && filterGroup.facet) {
      const filterName = facetToFilterRename(filterGroup.facet);
      const appliedFilter = filters[filterName];

      const numberOfAppliedFilters = Array.isArray(appliedFilter) ? appliedFilter.length : 0;

      if (filterGroup.facet === 'country' && !!filters.localCountries) {
        return numberOfAppliedFilters + 1;
      }

      return numberOfAppliedFilters;
    }
    if (filterGroup.type === HotFilterTypes.PRICE) {
      return filters.price ? Object.keys(filters.price)?.length : 0;
    }
    if (filterGroup.type === HotFilterTypes.SIZE) {
      const nrOfSelectedSizes = getSizeFilterSelectionCount(filters);
      if (areMySizesEnabled) {
        return nrOfSelectedSizes + 1;
      }
      return nrOfSelectedSizes;
    }

    if (filterGroup.type === HotFilterTypes.CATEGORY) {
      return categoryHotfilterCount;
    }

    if (filterGroup.type === HotFilterTypes.MATERIAL) {
      return materialHotfilterCount;
    }

    return 0;
  }, [filterGroup, filters, areMySizesEnabled, categoryHotfilterCount, materialHotfilterCount]);

  const handleClearFilters = () => {
    if (filterGroup.facet === 'country' && filters.localCountries) {
      const { localCountries: removedL2l, country: removedCountry, ...newFilters } = filters;
      setFilters(newFilters);
      return;
    }

    if (filterGroup.type === HotFilterTypes.ITEM && filterGroup.facet) {
      const filterName = facetToFilterRename(filterGroup.facet);
      const { [filterName]: removed, ...newFilters } = filters;
      setFilters(newFilters);
      return;
    }
    if (filterGroup.type === HotFilterTypes.PRICE) {
      const { price: removed, ...newFilters } = filters;
      setFilters(newFilters);
      return;
    }
    if (filterGroup.type === HotFilterTypes.SIZE) {
      const sizeFilters = sizeHotfilterFacetFields.map((facet) => facetToFilterRename(facet));
      const newFilters = sizeFilters.reduce((acc, filterName) => {
        if (filters[filterName]) {
          const { [filterName]: removed, ...rest } = acc;
          return rest;
        }
        return acc;
      }, filters);
      setFilters(newFilters);
      if (areMySizesEnabled) {
        setMySizesState((prev) => ({ ...prev, enabled: false }), true);
      }
      return;
    }

    if (filterGroup.type === HotFilterTypes.CATEGORY) {
      removeAllCategoryHotfilters();
      return;
    }

    if (filterGroup.type === HotFilterTypes.MATERIAL) {
      removeAllMaterialHotfilters();
      return;
    }
  };

  const isStatic = useMemo(() => {
    if (filterGroup.facet) {
      return staticFilterGroups[filterGroup.facet];
    }
    return false;
  }, [filterGroup, staticFilterGroups]);

  return { checkedItemCount, handleClearFilters, isStatic };
};

export default useCatalogHotfilterItem;
